import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Swiper, SwiperSlide } from "swiper/react";
import { Box, Button, CardMedia, Divider, Drawer, FormControlLabel, FormGroup, IconButton, Stack, Switch, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { Plus } from "lucide-react";
import { Autoplay, FreeMode, Navigation, Pagination, Thumbs } from "swiper/modules";
import { useAppDispatch, useAppSelector, useOrder, useResponsive } from "@/hooks";
import { closeItem } from "../../store/uiSlice";
import QuantitySelector from "../../components/quantity-selector/QuantitySelector";
import { toggleMenuItemAvailability } from "../organizations/slice";
import { useAccessControl } from "../access-control";
import ProductSettings from "./components/ProductSettings copy";
import { useFetchContentSettingsQuery } from "../organizations/api/settingsApi";
import useOrganizations from "../organizations/useOrganizations";
import ContactForm from "../organizations/components/contact-form/ContactForm";
import useOrganizationId from "@/hooks/useOrganizationId";
import { Content, OrderItem, Setting, Option } from "@/types";
import styled from "styled-components";
import Scrollbars from "react-custom-scrollbars-2";
import Price from "@/components/Price";
import { multiplyByQuantity } from "@/utils/numbers";

const SwiperStyled = styled(Swiper)`
  .swiper-pagination {
    bottom: 46px;
  }
`;

export interface SelectedSettings {
  [key: Setting["title"]]: Option["title"][];
}

interface Props {
  enableOrders?: boolean;
  onClose?: () => void;
  addItem?: (item: Content) => void;
}

const ProductDetailsPrinting = ({ enableOrders = false, onClose, addItem }: Props) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { currentOrganization } = useOrganizations();
  const organizationId = useOrganizationId();

  const selectedMenuItem = useAppSelector((state) => state.ui?.selectedMenuItem) as unknown as Content;

  const { hasAccess } = useAccessControl();

  const [isLoading, setIsLoading] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [selectedSettings, setSelectedSettings] = useState<SelectedSettings>({});
  const [thumbsSwiper, setThumbsSwiper] = useState(null);

  useEffect(() => {
    if (selectedMenuItem?.settings) {
      setSelectedSettings((selectedMenuItem?.settings as SelectedSettings) ?? {});
    }
  }, [selectedMenuItem]);

  const { data } = useFetchContentSettingsQuery({
    organizationId: organizationId ?? skipToken,
    contentId: selectedMenuItem?.id ?? skipToken,
  });

  const { addItems } = useOrder();
  const { isMobile } = useResponsive();

  const isFoodIndustry = currentOrganization?.industry === "food-and-beverages" ? !enableOrders : enableOrders;

  const handleClose = () => {
    setOrderItems([]);
    dispatch(closeItem());
    onClose?.();
  };

  const toggleAvailability = (event: ChangeEvent<HTMLInputElement>) => {
    if (!organizationId || !selectedMenuItem?.id) return;

    dispatch(
      toggleMenuItemAvailability({
        organizationId,
        menuItemId: selectedMenuItem?.id,
        availability: event.target.checked,
      })
    );
  };

  const price = useMemo(() => {
    return Number(selectedMenuItem?.price ?? 0);
  }, [selectedMenuItem?.price]);

  const totalItems = useMemo(() => orderItems.filter(({ id }) => id === selectedMenuItem?.id).length, [orderItems, selectedMenuItem?.id]);

  const onAdd = (item: OrderItem) => {
    setOrderItems((prev) => [...prev, item]);
  };

  const onRemove = (itemId: OrderItem["id"]) => {
    const draft = [...orderItems];
    if (!draft.length) return;

    const index = draft.findIndex(({ id }) => id === itemId);
    if (index === -1) return;

    draft.splice(index, 1);
    setOrderItems(draft);
  };

  const onSave = async () => {
    if (!organizationId) return;
    setIsLoading(true);

    await addItems(orderItems);

    setIsLoading(false);
    handleClose();
  };

  const handleAddItem = () => {
    const payload = {
      ...selectedMenuItem,
      settings: selectedSettings,
    };

    addItem?.(payload);

    handleClose();
  };

  return (
    <>
      <Drawer
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : "50%",
          },
        }}
        open={!!selectedMenuItem}
        onClose={handleClose}
        anchor="right"
        sx={{ position: "relative" }}
      >
        <Stack
          flex={1}
          sx={{
            boxSizing: "border-box",
          }}
        >
          <Scrollbars autoHide style={{ flex: 1 }}>
            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} p={2}>
              <Typography variant="h3" fontWeight={600}>
                {selectedMenuItem?.title ?? ""}
              </Typography>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
              {hasAccess("disable.dish") || hasAccess("enable.dish") ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormGroup onChange={toggleAvailability}>
                    <FormControlLabel
                      label={selectedMenuItem?.available ? "Disponible" : "Agotado"}
                      labelPlacement="start"
                      control={<Switch checked={selectedMenuItem?.available} color={selectedMenuItem?.available ? "success" : "error"} />}
                    />
                  </FormGroup>
                </Stack>
              ) : null}
            </Stack>

            <Box>
              <SwiperStyled
                spaceBetween={10}
                autoplay={{ delay: 3500, disableOnInteraction: false }}
                loop={true}
                pagination={{
                  clickable: true,
                }}
                navigation={true}
                modules={[Pagination, Navigation, FreeMode, Autoplay, Thumbs]}
                thumbs={{ swiper: thumbsSwiper }}
                centeredSlides={true}
                freeMode={true}
              >
                {(selectedMenuItem?.images ?? []).map((image) => (
                  <SwiperSlide key={image}>
                    <CardMedia
                      sx={{
                        height: 350,
                        width: "100%",
                        objectFit: isFoodIndustry ? "cover" : "contain",
                      }}
                      image={image}
                      component="img"
                      alt={image}
                    />
                  </SwiperSlide>
                ))}
              </SwiperStyled>
            </Box>

            <Stack
              p={3}
              spacing={2}
              sx={{
                mt: "-30px",
                zIndex: "30",
                backgroundColor: "white !important",
                position: "relative",
                borderTopLeftRadius: "24px",
                borderTopRightRadius: "24px",
              }}
            >
              {/* Contenido */}
              {isFoodIndustry ? (
                <QuantitySelector
                  total={() => totalItems}
                  onAdd={() => onAdd(selectedMenuItem as unknown as OrderItem)}
                  onRemove={() => onRemove(selectedMenuItem?.id ?? "")}
                  minimum={0}
                />
              ) : null}

              {isFoodIndustry && price ? (
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Precio:
                  </Typography>
                  <Typography>${price.toFixed(2)}</Typography>
                </Box>
              ) : null}

              {selectedMenuItem?.description ? (
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Detalles:
                  </Typography>
                  <Typography>{selectedMenuItem?.description}</Typography>
                </Box>
              ) : null}

              {isFoodIndustry ? (
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {isFoodIndustry ? "Ingredientes" : "Elementos"}
                  </Typography>
                  <ul>
                    {(selectedMenuItem?.elements ?? []).map((element, elementIndex) => (
                      <li key={`element-${elementIndex}`}>
                        <Typography variant="body1">{element}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              ) : null}

              {selectedMenuItem?.quantity ? (
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Cantidad:
                  </Typography>
                  <Typography>{selectedMenuItem?.quantity}</Typography>
                </Box>
              ) : null}
              <Divider>Configuración</Divider>
              <ProductSettings
                settings={data}
                selectedSettings={selectedSettings} // <== redux
                setSelectedSettings={setSelectedSettings}
              />
            </Stack>
          </Scrollbars>
          <Stack
            direction={"row"}
            spacing={2}
            sx={{
              background: "white",
              padding: 2,
              position: "relative",
              zIndex: "30",
              boxShadow: "0px -2px 8px #0000001f",
            }}
          >
            <Button fullWidth variant="outlined" onClick={handleClose}>
              {orderItems?.length ? "Cancelar" : "Cerrar"}
            </Button>
            {isFoodIndustry ? (
              <Button fullWidth color="success" endIcon={!totalItems && <Plus />} onClick={onSave} disabled={isLoading || totalItems === 0}>
                <Price currency="NIO" preffix="Añadir">
                  {multiplyByQuantity(totalItems, selectedMenuItem.price)}
                </Price>
              </Button>
            ) : (
              <Button fullWidth color="success" onClick={handleAddItem}>
                {pathname === "/checkout" ? "Actualizar" : "Agregar"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Drawer>

      <ContactForm />
    </>
  );
};

export default ProductDetailsPrinting;
